// src/services/appConfigService.js
import api from "../components/Api/Api";

class AppConfigService {

  /**
   * Obtener lista de configuraciones
   * @param {Object} params - Parametros de consulta (search, type, is_active, is_sensitive)
   * @returns {Promise<Object>} Lista de configuraciones
   */
  async getAll(params = {}) {
    try {
      const response = await api.get("/api/app-configs", { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error("Error fetching app configs:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Obtener una configuracion por ID
   * @param {number} id - ID de la configuracion
   * @returns {Promise<Object>} Datos de la configuracion
   */
  async getById(id) {
    try {
      const response = await api.get(`/api/app-configs/${id}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error("Error fetching app config:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Crear una nueva configuracion
   * @param {Object} configData - Datos de la configuracion
   * @returns {Promise<Object>} Configuracion creada
   */
  async create(configData) {
    try {
      const response = await api.post("/api/app-configs", configData);
      return {
        success: true,
        data: response.data,
        message: "Configuracion creada exitosamente"
      };
    } catch (error) {
      console.error("Error creating app config:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Actualizar una configuracion
   * @param {number} id - ID de la configuracion
   * @param {Object} configData - Datos actualizados
   * @returns {Promise<Object>} Configuracion actualizada
   */
  async update(id, configData) {
    try {
      const response = await api.put(`/api/app-configs/${id}`, configData);
      return {
        success: true,
        data: response.data,
        message: "Configuracion actualizada exitosamente"
      };
    } catch (error) {
      console.error("Error updating app config:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Actualizar parcialmente una configuracion
   * @param {number} id - ID de la configuracion
   * @param {Object} configData - Datos a actualizar
   * @returns {Promise<Object>} Configuracion actualizada
   */
  async patch(id, configData) {
    try {
      const response = await api.patch(`/api/app-configs/${id}`, configData);
      return {
        success: true,
        data: response.data,
        message: "Configuracion actualizada exitosamente"
      };
    } catch (error) {
      console.error("Error patching app config:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Eliminar una configuracion
   * @param {number} id - ID de la configuracion
   * @returns {Promise<Object>} Confirmacion de eliminacion
   */
  async delete(id) {
    try {
      const response = await api.delete(`/api/app-configs/${id}`);
      return {
        success: true,
        message: "Configuracion eliminada exitosamente"
      };
    } catch (error) {
      console.error("Error deleting app config:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Alternar estado activo de una configuracion
   * @param {number} id - ID de la configuracion
   * @returns {Promise<Object>} Configuracion con estado actualizado
   */
  async toggleActive(id) {
    try {
      const response = await api.patch(`/api/app-configs/${id}/toggle`);
      return {
        success: true,
        data: response.data,
        message: "Estado actualizado exitosamente"
      };
    } catch (error) {
      console.error("Error toggling app config:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Actualizacion masiva de configuraciones
   * @param {Array} configs - Array de configuraciones a actualizar
   * @returns {Promise<Object>} Resultado de la actualizacion masiva
   */
  async bulkUpdate(configs) {
    try {
      const response = await api.post("/api/app-configs/bulk-update", { configs });
      return {
        success: true,
        data: response.data,
        message: "Configuraciones actualizadas exitosamente"
      };
    } catch (error) {
      console.error("Error bulk updating app configs:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Obtener valor de configuracion (endpoint publico)
   * @param {string} key - Clave de la configuracion
   * @returns {Promise<Object>} Valor de la configuracion
   */
  async getValue(key) {
    try {
      const response = await api.get(`/api/config/${key}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error("Error getting config value:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Manejo centralizado de errores
   * @param {Error} error - Error capturado
   * @returns {Object} Error formateado
   */
  handleError(error) {
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 422:
          return {
            type: 'validation',
            errors: data.errors || {},
            message: data.message || "Error de validacion"
          };
        case 404:
          return {
            type: 'not_found',
            message: "Configuracion no encontrada"
          };
        case 403:
          return {
            type: 'forbidden',
            message: "No tiene permisos para realizar esta accion"
          };
        case 401:
          return {
            type: 'unauthorized',
            message: "Sesion expirada. Inicie sesion nuevamente"
          };
        case 500:
          return {
            type: 'server',
            message: "Error interno del servidor"
          };
        default:
          return {
            type: 'api',
            message: data.message || "Error en el servidor"
          };
      }
    } else if (error.request) {
      return {
        type: 'network',
        message: "Error de conexion. Verifique su conexion a internet."
      };
    } else {
      return {
        type: 'unknown',
        message: "Error inesperado. Intente nuevamente."
      };
    }
  }

  /**
   * Validar datos de configuracion antes del envio
   * @param {Object} data - Datos a validar
   * @returns {Object} Resultado de validacion
   */
  validateData(data) {
    const errors = {};
    const requiredFields = ['key', 'type'];

    // Validar campos requeridos
    requiredFields.forEach(field => {
      if (!data[field] || (typeof data[field] === 'string' && data[field].trim() === '')) {
        errors[field] = `El campo ${field} es requerido`;
      }
    });

    // Validar formato de clave
    if (data.key && !/^[a-z][a-z0-9_]*$/.test(data.key)) {
      errors.key = "La clave debe contener solo minusculas, numeros y guiones bajos";
    }

    // Validar tipo
    const validTypes = ['string', 'json', 'boolean', 'integer'];
    if (data.type && !validTypes.includes(data.type)) {
      errors.type = "El tipo debe ser uno de: string, json, boolean, integer";
    }

    // Validar longitud de descripcion
    if (data.description && data.description.length > 1000) {
      errors.description = "La descripcion no puede exceder 1000 caracteres";
    }

    // Validar valor segun tipo
    if (data.value && data.type) {
      switch (data.type) {
        case 'boolean':
          if (data.value !== 'true' && data.value !== 'false' && data.value !== '1' && data.value !== '0') {
            errors.value = "Para tipo boolean, el valor debe ser true, false, 1 o 0";
          }
          break;
        case 'integer':
          if (isNaN(parseInt(data.value))) {
            errors.value = "Para tipo integer, el valor debe ser un numero";
          }
          break;
        case 'json':
          try {
            JSON.parse(data.value);
          } catch (e) {
            errors.value = "Para tipo json, el valor debe ser JSON valido";
          }
          break;
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  /**
   * Filtrar datos sensibles para mostrar
   * @param {Array} configs - Array de configuraciones
   * @returns {Array} Configuraciones con datos sensibles enmascarados
   */
  maskSensitiveData(configs) {
    return configs.map(config => ({
      ...config,
      value: config.is_sensitive ? "********" : config.value
    }));
  }

  /**
   * Obtener estadisticas de configuraciones
   * @param {Array} configs - Array de configuraciones
   * @returns {Object} Estadisticas
   */
  getStats(configs) {
    return {
      total: configs.length,
      active: configs.filter(c => c.is_active).length,
      inactive: configs.filter(c => !c.is_active).length,
      sensitive: configs.filter(c => c.is_sensitive).length,
      byType: configs.reduce((acc, config) => {
        acc[config.type] = (acc[config.type] || 0) + 1;
        return acc;
      }, {})
    };
  }
}

export default new AppConfigService();